import type { BevelRenderSettings } from '../utils/getBevelDepth';
import { bevelContourTable } from '../utils/getBevelDepth';

// Reconstruct the lighting surface independently of the painted bevel width.
// These are sampling footprints, not additional authored Size/Softness.
export const BEVEL_NORMAL_SIGMA = 1.25;
export const BEVEL_SURFACE_SIGMA = 0.87;

function normalQuantile(fraction: number) {
  const x = 2 * fraction - 1;
  const a = 0.147;
  const log = Math.log(1 - x * x);
  const first = 2 / (Math.PI * a) + log / 2;
  return (
    Math.SQRT2 *
    Math.sign(x) *
    Math.sqrt(Math.max(0, Math.sqrt(first * first - log / a) - first))
  );
}

type SurfaceSettings = Pick<
  BevelRenderSettings,
  'technique' | 'size' | 'softness' | 'contour' | 'contourRange'
>;

export function getBevelSurface(depth: SurfaceSettings) {
  const sigma = Math.max(BEVEL_NORMAL_SIGMA, depth.size / 3);
  // SourceAlpha represents pixel coverage, including the square-pixel footprint.
  const distanceScale = Math.sqrt(sigma * sigma + 1 / 12);
  const height: number[] = [];
  const inset: number[] = [];
  for (let index = 0; index <= 1024; index++) {
    const fraction = index / 1024;
    const distance =
      index === 0
        ? -Infinity
        : index === 1024
        ? Infinity
        : distanceScale * normalQuantile(fraction);
    const ramp = Math.max(0, Math.min(1, distance / depth.size));
    // Smooth rounds the lighting surface; the painted inset stays unchanged.
    height.push(
      depth.technique === 'smooth' ? ramp * ramp * (3 - 2 * ramp) : ramp
    );
    // Coverage of the inward boundary over one pixel. Keeping this separate
    // from surface smoothing prevents a thicker shadow at subpixel sizes.
    inset.push(Math.max(0, Math.min(1, distance - depth.size + 0.5)));
  }
  return {
    sigma,
    height: height.join(' '),
    inset: inset.join(' '),
    contour: bevelContourTable(depth.contour, depth.contourRange),
    surfaceSigma: Math.hypot(depth.softness, BEVEL_SURFACE_SIGMA),
  };
}
